// ============================================
// Keycloak Auth Service for Dashboard
// ============================================

export interface AuthConfig {
  authMode: 'local' | 'oidc';
  keycloak?: {
    url: string;
    realm: string;
    clientId: string;
    accountUrl?: string;
    adminUrl?: string;
  };
}

let cachedConfig: AuthConfig | null = null;

const KEYCLOAK_STATE_KEY = 'kern_keycloak_state';
const KEYCLOAK_VERIFIER_KEY = 'kern_keycloak_verifier';
const KEYCLOAK_REDIRECT_KEY = 'kern_keycloak_redirect';

/**
 * Fetch auth configuration from the backend.
 * Caches the result for the lifetime of the page.
 */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  if (cachedConfig) return cachedConfig;

  const response = await fetch('/api/v1/auth/config');
  if (!response.ok) {
    throw new Error('Fehler beim Laden der Auth-Konfiguration');
  }

  cachedConfig = (await response.json()) as AuthConfig;
  return cachedConfig;
}

export function clearAuthConfigCache(): void {
  cachedConfig = null;
}

/**
 * Initiate Keycloak login by redirecting to the authorization endpoint.
 * Stores state and code verifier in sessionStorage for callback validation.
 */
export async function initiateKeycloakLogin(): Promise<void> {
  const callbackUrl = `${window.location.origin}/login/callback`;

  const response = await fetch(
    `/api/v1/auth/keycloak/login?redirect_uri=${encodeURIComponent(callbackUrl)}`
  );
  if (!response.ok) {
    throw new Error('Fehler beim Starten der SSO-Anmeldung');
  }

  const data = await response.json();

  // Store PKCE state in sessionStorage (cleared after callback)
  sessionStorage.setItem(KEYCLOAK_STATE_KEY, data.state);
  sessionStorage.setItem(KEYCLOAK_VERIFIER_KEY, data.codeVerifier);
  sessionStorage.setItem(KEYCLOAK_REDIRECT_KEY, callbackUrl);

  // Redirect to Keycloak
  window.location.href = data.authUrl;
}

/**
 * Handle the Keycloak callback after redirect.
 * Validates state, exchanges code for token.
 */
export async function handleKeycloakCallback(
  code: string,
  state: string
): Promise<{ token: string; user: { id: number; email: string; name: string; role: string } }> {
  // Validate state
  const storedState = sessionStorage.getItem(KEYCLOAK_STATE_KEY);
  if (!storedState || storedState !== state) {
    cleanupSessionStorage();
    throw new Error('Ungültiger Authentifizierungsstatus');
  }

  const codeVerifier = sessionStorage.getItem(KEYCLOAK_VERIFIER_KEY);
  const redirectUri = sessionStorage.getItem(KEYCLOAK_REDIRECT_KEY);

  if (!codeVerifier || !redirectUri) {
    cleanupSessionStorage();
    throw new Error('Fehlende Authentifizierungsdaten');
  }

  // Clean up session storage
  cleanupSessionStorage();

  // Exchange code for token via backend
  const response = await fetch('/api/v1/auth/keycloak/callback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri, codeVerifier }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'OIDC-Authentifizierung fehlgeschlagen');
  }

  return response.json();
}

/**
 * Fetch the Keycloak logout URL from the backend.
 */
export async function fetchKeycloakLogoutUrl(): Promise<string> {
  const redirectUri = window.location.origin + '/login';
  const response = await fetch(
    `/api/v1/auth/keycloak/logout-url?redirect_uri=${encodeURIComponent(redirectUri)}`
  );
  if (!response.ok) {
    throw new Error('Fehler beim Laden der Logout-URL');
  }
  const data = await response.json();
  return data.logoutUrl;
}

function cleanupSessionStorage(): void {
  sessionStorage.removeItem(KEYCLOAK_STATE_KEY);
  sessionStorage.removeItem(KEYCLOAK_VERIFIER_KEY);
  sessionStorage.removeItem(KEYCLOAK_REDIRECT_KEY);
}
